import React from 'react';
import { CheckIcon, GiftIcon, WrenchIcon } from 'lucide-react';
import { SectionHeading } from '../SectionHeading';
import { CheckoutButton } from '../CheckoutButton';
import { COMMERCIAL_RIGHTS, HERO_MOCKUP, OFFER_FEATURES, VOLUMES } from '../../data/welding';
import { PRICE } from '../../data/checkout';

/**
 * Value stack + first price reveal (#offer). The hero CTA scrolls here.
 * `#offer-price` and `#offer-cta` drive the mobile sticky bar.
 */
export function OfferSection() {
  return <section id="offer" aria-labelledby="offer-title" className="blueprint-grid bg-charcoal py-14 sm:py-20 lg:py-24" data-id="element-180" data-mp="1pybkwsjk1iq">
      <div className="mx-auto w-full max-w-5xl px-5 sm:px-8" data-id="element-181" data-mp="3sxudf0aglhgd">
        <SectionHeading id="offer-title" align="center" title="GET THE COMPLETE WORKSHOP PLAN COLLECTION" data-id="element-182" data-mp="3nxpwj95n2kpm" />

        <div className="mt-8 grid overflow-hidden rounded-[14px] bg-surface shadow-[0_40px_80px_-40px_rgba(0,0,0,0.9)] ring-1 ring-arc/25 sm:mt-10 lg:grid-cols-2" data-id="element-183" data-mp="3iyd7qiv7j3ke">
          <div className="hidden items-center justify-center border-r border-white/10 p-8 lg:flex" data-id="element-184" data-mp="3ez4erg7zr9pw">
            <img src={HERO_MOCKUP.src} alt={HERO_MOCKUP.alt} width={HERO_MOCKUP.width} height={HERO_MOCKUP.height} loading="lazy" decoding="async" className="max-h-[520px] w-auto max-w-full object-contain drop-shadow-[0_24px_30px_rgba(0,0,0,0.5)]" data-id="element-185" data-mp="1apxoeudfahjm" />
          </div>

          <div className="px-5 pb-7 pt-6 sm:px-10 sm:pb-10 sm:pt-9" data-id="element-186" data-mp="1tj1k0la7b4gv">
            <p className="wordmark text-[1.55rem] text-white sm:text-[1.9rem]" data-id="element-187" data-mp="1yhuxwetegxhk">
              Welding Project Plans<sup className="ml-0.5 font-sans text-[0.45em] font-semibold" data-id="element-188" data-mp="1kilgjbtgjxc0">™</sup>
            </p>
            <p className="mt-2 font-heading text-[1.2rem] font-semibold uppercase tracking-[0.03em] text-orange" data-id="element-189" data-mp="1oinkjrmo12ug">
              30 projects across 6 digital volumes
            </p>

            <ul className="mt-5 grid grid-cols-6 gap-1.5 lg:hidden" aria-label="The six volumes" data-id="element-190" data-mp="d40dbi1dhehp">
              {VOLUMES.map(volume => <li key={volume.number} data-id="element-191" data-mp="2fqe0jr8hkkwb">
                  <img src={volume.src} alt={volume.alt} width={volume.width} height={volume.height} loading="lazy" decoding="async" className="aspect-square w-full rounded-[3px] object-contain ring-1 ring-white/10" data-id="element-192" data-mp="2arucnlac0z1b" />
                </li>)}
            </ul>

            <ul className="mt-5 grid gap-3" data-id="element-193" data-mp="1s136stpjzqro">
              {OFFER_FEATURES.map(feature => <li key={feature} className="flex items-start gap-3 text-[1rem] font-medium text-white" data-id="element-194" data-mp="1n1ginp86sq43">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-orange text-charcoal" aria-hidden data-id="element-195" data-mp="1i3yfygkrj74j">
                    <CheckIcon className="h-3 w-3" strokeWidth={3} data-id="element-196" data-mp="3ci97iwsqsfe0" />
                  </span>
                  {feature}
                </li>)}
            </ul>

            <p className="mt-5 flex items-center gap-3 rounded-[8px] bg-arc/10 px-4 py-3 text-[1rem] font-semibold text-white ring-1 ring-arc/35" data-id="element-197" data-mp="3ozk1jfebolgn">
              <GiftIcon className="h-5 w-5 shrink-0 text-arc-light" aria-hidden data-id="element-198" data-mp="3tr20xz0p6dzi" />
              Plus all 5 bonus resources
            </p>

            <p className="mt-3 flex items-start gap-3 rounded-[8px] bg-arc/10 px-4 py-3 ring-1 ring-arc/35" data-id="element-199" data-mp="31zcg5u3xzdsi">
              <WrenchIcon className="mt-0.5 h-5 w-5 shrink-0 text-arc-light" aria-hidden data-id="element-200" data-mp="360o4zxezruzn" />
              <span data-id="element-201" data-mp="3b00w4ucwbo81">
                <span className="label-tech block text-arc-light" data-id="element-202" data-mp="3azrcrpmcwfbb">{COMMERCIAL_RIGHTS.label}</span>
                <span className="mt-1 block text-[0.92rem] leading-snug text-white" data-id="element-203" data-mp="3fzdr62dx81lp">{COMMERCIAL_RIGHTS.sentence}</span>
              </span>
            </p>

            <div id="offer-price" className="mt-6 border-t border-white/10 pt-6 text-center" data-id="element-204" data-mp="qbw9i0b1b9a1">
              <p className="font-heading text-[4.2rem] font-bold leading-none tracking-[-0.01em] text-white sm:text-[4.8rem]" data-id="element-205" data-mp="vazgvrehlrl6">
                {PRICE.label}
              </p>
              <p className="mt-2 text-[0.95rem] text-steel-pale" data-id="element-206" data-mp="1z81pywnd0doy">{PRICE.note}</p>
              <p className="mx-auto mt-2 max-w-[34ch] text-[0.9rem] leading-snug text-steel-light" data-id="element-207" data-mp="2d7kocxqdcao2">
                Receive all 6 PDF volumes and 5 bonus resources by email immediately after payment.
              </p>
            </div>

            <CheckoutButton id="offer-cta" label="GET THE COMPLETE COLLECTION" size="lg" className="mt-6 w-full" data-id="element-208" data-mp="2n2uo1etao5yo" />

            <p className="mt-4 text-center text-[0.86rem] leading-relaxed text-steel-light" data-id="element-209" data-mp="2i3rv2sql9q72">
              Digital plans for building workshop equipment. Physical equipment and tools are not included.
            </p>
          </div>
        </div>
      </div>
    </section>;
}